import { getArticlesSuggestions, newsAndTrends, learningCenter } from 'constants/routes';

/**
 * Generates the URL for a "News and Trends" article.
 * @param {Object} article - The article object.
 * @param {string} article.slug - The slug of the article.
 * @param {string} [article.subLandingParentSlug] - The slug of the parent sub-landing page (optional).
 * @returns {string} The URL for the "News and Trends" article.
 */
const getNewsAndTrendsArticleUrl = function (article) {
  const { slug, subLandingParentSlug } = article;

  if (subLandingParentSlug) {
    return `${newsAndTrends}/${subLandingParentSlug}/${slug}/`;
  }

  return `${newsAndTrends}/${slug}/`;
};

/**
 * Generates the URL for a "Learning Center" article.
 * @param {Object} article - The article object.
 * @param {string} article.slug - The slug of the article.
 * @returns {string} The URL for the "Learning Center" article.
 */
const getLearningCenterArticleUrl = function (article) {
  const { slug } = article;

  return `${learningCenter}/${slug}/`;
};

/**
 * Mapping of content clients to their respective URL generation functions.
 */
const articleUrl = {
  learn: getLearningCenterArticleUrl,
  news: getNewsAndTrendsArticleUrl,
  default: getLearningCenterArticleUrl,
};

/**
 * Generates the URL for an article based on the provided article data.
 * @param {Object} articleData - The data object containing information about the article.
 * @returns {string} The generated URL for the article.
 */
export const getArticlePageUrl = function(articleData){
  const { contentClient } = articleData;
  const getArticleUrlFunction = articleUrl[contentClient] || articleUrl.default;
  return getArticleUrlFunction(articleData);
};

/**
 * Constructs the URL for fetching article suggestions based on the provided content client.
 * @param {string} contentClient - The identifier for the content client to be used in the request.
 * @returns {string} The constructed URL with the encoded content client parameter.
 */
export const getArticleSuggestionsUrl = function(contentClient) {
  return `${getArticlesSuggestions}?contentClient=${encodeURIComponent(contentClient)}`
};

/**
 * Generates the URL for the search results page based on the provided search term and optional page parameter.
 * @param {string} searchTerm - The term to search for.
 * @param {string} [fromPageParameter] - An optional parameter indicating the originating page.
 * @param {string} [contentClientParameter] - An optional parameter indicating the content client.
 * @returns {string} The constructed URL for the search results page.
 */
export const getArticleSearchResultPageUrl = function(searchTerm, fromPageParameter, contentClientParameter) {
  const fromPage = fromPageParameter ? `&fromPage=${encodeURIComponent(fromPageParameter)}` : '';
  const contentClient = contentClientParameter ? `&contentClient=${encodeURIComponent(contentClientParameter)}` : '';
  const url = `${learningCenter}?searchTerm=${encodeURIComponent(searchTerm)}${fromPage}${contentClient}`;
  return url;
};